import {
  findVersionIdCoveringDate,
  zipMasterRecordId,
} from '../YhcServiceLevelList/masterVersionSpanNotes'
import type { ModelYhcRegularWorkingDays } from '../../fireModels/yhcDataTypes'
import type { YhcMasterVersionPeriod } from './masterVersionByDate'

const JPN_WEEKDAYS = ['日', '月', '火', '水', '木', '金', '土']

export type WorkingDayRecordLike = {
  pattern_name?: string | null
  enabled_from?: string | null
  disabled_at?: string | null
  regular_weekdays?: string[] | null
  fallback_pattern_name?: string | ModelYhcRegularWorkingDays | null
}

export type WorkingDaysByDateInput = {
  periods: Array<Pick<YhcMasterVersionPeriod, 'id' | 'enabledFrom' | 'disabledAt'>>
  patternNameByVersionId: Record<string, string>
  records: WorkingDayRecordLike[]
  useFallback?: boolean
}

export type FixedHolidayLike = {
  start_month_day?: string | null
  end_month_day?: string | null
}

const toYmd = (value: string | null | undefined): string => (value || '').trim().slice(0, 10)

/**
 * datepicker は Date を返すため、ローカル日付の YYYY-MM-DD に正規化する。
 */
export const toCalendarYmd = (date: string | Date | null | undefined): string => {
  if (date == null || date === '') return ''
  if (date instanceof Date) {
    if (Number.isNaN(date.getTime())) return ''
    const y = date.getFullYear()
    const m = String(date.getMonth() + 1).padStart(2, '0')
    const d = String(date.getDate()).padStart(2, '0')
    return `${y}-${m}-${d}`
  }
  const day = String(date).trim().slice(0, 10)
  return /^\d{4}-\d{2}-\d{2}$/.test(day) ? day : ''
}

const jpnWeekdayFromYmd = (ymd: string): string => {
  const d = new Date(`${ymd}T00:00:00`)
  if (Number.isNaN(d.getTime())) return ''
  return JPN_WEEKDAYS[d.getDay()] || ''
}

const weekdaysOfRecord = (record: WorkingDayRecordLike | null | undefined): string[] =>
  Array.isArray(record?.regular_weekdays) ? record.regular_weekdays : []

const recordCoversDate = (record: WorkingDayRecordLike, ymd: string): boolean => {
  const from = toYmd(record.enabled_from)
  const until = toYmd(record.disabled_at)
  return !!from && !!until && from <= ymd && until >= ymd
}

const pickCoveringRecord = (
  records: WorkingDayRecordLike[],
  patternName: string,
  ymd: string,
): WorkingDayRecordLike | null => {
  const covering = records.filter(
    (record) =>
      (record.pattern_name || '').trim() === patternName && recordCoversDate(record, ymd),
  )
  covering.sort((a, b) => toYmd(b.enabled_from).localeCompare(toYmd(a.enabled_from)))
  return covering[0] || null
}

const pickLatestRecordEnabledOnOrBefore = (
  records: WorkingDayRecordLike[],
  patternName: string,
  ymd: string,
): WorkingDayRecordLike | null => {
  const matching = records.filter((record) => {
    if ((record.pattern_name || '').trim() !== patternName) return false
    const from = toYmd(record.enabled_from)
    return !!from && from <= ymd
  })
  matching.sort((a, b) => toYmd(b.enabled_from).localeCompare(toYmd(a.enabled_from)))
  return matching[0] || null
}

const hasZipVersionKey = (
  patternNameByVersionId: Record<string, unknown>,
  versionId: string,
): boolean => Object.prototype.hasOwnProperty.call(patternNameByVersionId, versionId)

/**
 * 指定版に zip キーが無いときは、enabledFrom が同じか前の版のうち
 * zip がある最新の版 ID を返す。キーがある版が無ければ元の versionId。
 */
export const resolveVersionIdWithZipFallback = (
  versionId: string,
  periods: WorkingDaysByDateInput['periods'],
  versionIdsWithZip: Record<string, unknown>,
): string => {
  if (!versionId) return ''
  if (hasZipVersionKey(versionIdsWithZip, versionId)) return versionId
  const coveringFrom = toYmd(
    (periods || []).find((period) => period.id === versionId)?.enabledFrom,
  )
  if (!coveringFrom) return versionId
  const previous = [...(periods || [])]
    .filter((period) => !!period.id && toYmd(period.enabledFrom) <= coveringFrom)
    .sort((a, b) => toYmd(b.enabledFrom).localeCompare(toYmd(a.enabledFrom)))
  for (const period of previous) {
    if (!hasZipVersionKey(versionIdsWithZip, period.id)) continue
    return period.id
  }
  return versionId
}

/**
 * 覆っている版の zip があるときはそのパターン名（空文字＝週7）。
 * 版が無い／zip キーが無いときは、zip がある直前の版へフォールバックする。
 */
export const resolvePatternNameForDate = (
  ymd: string,
  periods: WorkingDaysByDateInput['periods'],
  patternNameByVersionId: Record<string, string>,
): string => {
  const coveringId = findVersionIdCoveringDate(periods, ymd)
  if (coveringId && hasZipVersionKey(patternNameByVersionId, coveringId)) {
    return (patternNameByVersionId[coveringId] || '').trim()
  }
  const previous = [...(periods || [])]
    .filter((period) => !!period.id && toYmd(period.enabledFrom) <= ymd)
    .sort((a, b) => toYmd(b.enabledFrom).localeCompare(toYmd(a.enabledFrom)))
  for (const period of previous) {
    if (!hasZipVersionKey(patternNameByVersionId, period.id)) continue
    return (patternNameByVersionId[period.id] || '').trim()
  }
  return ''
}

export const zipLookupCandidates = (zip: string): string[] => {
  const trimmed = (zip || '').trim()
  const digits = trimmed.replace(/\D/g, '')
  const values = new Set<string>()
  if (trimmed) values.add(trimmed)
  if (digits) values.add(digits)
  if (digits.length === 7) values.add(`${digits.slice(0, 3)}-${digits.slice(3)}`)
  return [...values]
}

export const normalizeZipCacheKey = (zip: string): string =>
  (zip || '').replace(/\D/g, '')

/**
 * 郵便番号マスタを主キーで引くための id 一覧。
 * 全版 × 郵便番号候補（数字のみ / 7桁はハイフン付き）。版が無ければ空。
 */
export const zipMasterRecordIdsForLookup = (
  zip: string,
  periods: Array<{ id?: string | null }> | null | undefined,
): string[] => {
  const versionIds = (periods || [])
    .map((period) => String(period?.id || '').trim())
    .filter((id) => !!id)
  const candidates = zipLookupCandidates(zip)
  if (versionIds.length === 0 || candidates.length === 0) return []
  const ids = new Set<string>()
  for (const versionId of versionIds) {
    for (const candidate of candidates) {
      ids.add(zipMasterRecordId(versionId, candidate))
    }
  }
  return [...ids]
}

/**
 * パターン名と基準日が期間内の営業日レコード。期間外なら null（latest-before には倒さない）。
 */
export const pickCoveringWorkingDayRecord = (
  records: WorkingDayRecordLike[] | null | undefined,
  patternName: string,
  date: string | Date | null | undefined,
): WorkingDayRecordLike | null => {
  const ymd = toCalendarYmd(date)
  const name = (patternName || '').trim()
  if (!ymd || !name) return null
  return pickCoveringRecord(records || [], name, ymd)
}

export const pickFirstDefined = <T>(values: Array<T | null | undefined>): T | null => {
  for (const value of values) {
    if (value != null) return value
  }
  return null
}

export const versionIdOfZipRecord = (
  record: { id?: unknown; version?: unknown } | null | undefined,
): string => {
  const value = record?.version
  if (value && typeof value === 'object' && 'id' in (value as object)) {
    const nestedId = String((value as { id?: unknown }).id || '').trim()
    if (nestedId) return nestedId
  }
  const asString = String(value || '').trim()
  if (asString) return asString
  const recordId = String(record?.id || '').trim()
  const sep = recordId.lastIndexOf('--')
  return sep > 0 ? recordId.slice(0, sep) : ''
}

/**
 * zip の全版レコードから、指定版の1件を返す。その版に無ければ直前版。
 */
export const pickZipRecordForVersion = <T extends { version?: unknown }>(
  records: T[] | null | undefined,
  versionId: string,
  periods: WorkingDaysByDateInput['periods'],
): T | null => {
  const map: Record<string, T> = {}
  for (const record of records || []) {
    const vid = versionIdOfZipRecord(record)
    if (!vid) continue
    map[vid] = record
  }
  const resolved = resolveVersionIdWithZipFallback(versionId, periods, map)
  return (resolved && map[resolved]) || null
}

export const normalizeWeekdaySetKey = (weekdays: string[] | null | undefined): string => {
  const days = weekdays || []
  if (days.length === 0 || days.length === 7) return '*'
  return [...new Set(days)].sort().join(',')
}

/**
 * 基準日より後で、発地の営業曜日セットが変わる最初の日。
 * 候補はマスタ版開始日と営業日レコードの enabled_from のみ。
 */
export const findNextWorkingDaysChangeYmd = (
  fromYmd: string | Date | null | undefined,
  input: WorkingDaysByDateInput,
): string | null => {
  const today = toCalendarYmd(fromYmd)
  if (!today) return null
  const currentKey = normalizeWeekdaySetKey(resolveRegularWeekdaysForDate(today, input))
  const candidates = new Set<string>()
  for (const period of input.periods || []) {
    const day = toYmd(period.enabledFrom)
    if (day > today) candidates.add(day)
  }
  for (const record of input.records || []) {
    const day = toYmd(record.enabled_from)
    if (day > today) candidates.add(day)
  }
  for (const day of [...candidates].sort()) {
    const nextKey = normalizeWeekdaySetKey(resolveRegularWeekdaysForDate(day, input))
    if (nextKey !== currentKey) return day
  }
  return null
}

const resolveFallbackWeekdays = (
  record: WorkingDayRecordLike,
  records: WorkingDayRecordLike[],
  ymd: string,
): string[] | null => {
  const fallback = record.fallback_pattern_name
  if (!fallback) return null
  if (typeof fallback === 'object') {
    return weekdaysOfRecord(fallback)
  }
  const fallbackName = String(fallback).trim()
  if (!fallbackName) return null
  const fallbackRecord =
    pickCoveringRecord(records, fallbackName, ymd) ||
    pickLatestRecordEnabledOnOrBefore(records, fallbackName, ymd)
  return fallbackRecord ? weekdaysOfRecord(fallbackRecord) : null
}

/**
 * その日のパターン名と営業日レコードから曜日を返す。
 * 覆う版に zip キーが無い／版が無いときは zip がある直前版へフォールバックする。
 * 覆うレコードが無いときは enabled_from が当日以前の最新レコードを使う。
 * パターンなし・レコードなしは空配列（週7＝制限なし）。
 */
export const resolveRegularWeekdaysForDate = (
  date: string | Date | null | undefined,
  input: WorkingDaysByDateInput,
): string[] => {
  const ymd = toCalendarYmd(date)
  if (!ymd) return []
  const patternName = resolvePatternNameForDate(
    ymd,
    input.periods,
    input.patternNameByVersionId,
  )
  if (!patternName) return []
  const records = input.records || []
  const record =
    pickCoveringRecord(records, patternName, ymd) ||
    pickLatestRecordEnabledOnOrBefore(records, patternName, ymd)
  if (!record) return []
  if (input.useFallback) {
    const fallbackWeekdays = resolveFallbackWeekdays(record, records, ymd)
    if (fallbackWeekdays) return fallbackWeekdays
  }
  return weekdaysOfRecord(record)
}

/**
 * 営業曜日が 1〜6 件のとき、その曜日でなければ定休。
 * 空または 7 件は毎日営業（制限なし）。
 */
export const isRegularNonWorkingDay = (
  date: string | Date | null | undefined,
  weekdays: string[] | null | undefined,
): boolean => {
  const ymd = toCalendarYmd(date)
  const days = weekdays || []
  const hasRegularPattern = days.length > 0 && days.length < 7
  if (!hasRegularPattern || !ymd) return false
  const dayOfWeek = jpnWeekdayFromYmd(ymd)
  return !!dayOfWeek && !days.includes(dayOfWeek)
}

export const isFixedHolidayYmd = (
  date: string | Date | null | undefined,
  holidays: FixedHolidayLike[] | null | undefined,
): boolean => {
  const ymd = toCalendarYmd(date)
  const monthDay = ymd.slice(5, 10)
  if (!/^\d{2}-\d{2}$/.test(monthDay)) return false
  return (holidays || []).some((holiday) => {
    const startMD = holiday.start_month_day
    const endMD = holiday.end_month_day
    if (!startMD || !endMD) return false
    if (startMD > endMD) {
      return monthDay >= startMD || monthDay <= endMD
    }
    return monthDay >= startMD && monthDay <= endMD
  })
}

export const isCalendarDateDisabled = (
  date: string | Date | null | undefined,
  weekdays: string[] | null | undefined,
  holidays: FixedHolidayLike[] | null | undefined,
): boolean => {
  return isRegularNonWorkingDay(date, weekdays) || isFixedHolidayYmd(date, holidays)
}

const addOneCalendarDay = (ymd: string): string => {
  const d = new Date(`${ymd}T00:00:00`)
  if (Number.isNaN(d.getTime())) return ''
  d.setDate(d.getDate() + 1)
  return toCalendarYmd(d)
}

/**
 * 基準日が定休・年末年始なら、選択可能な翌営業日を返す。基準日が営業日ならそのまま。
 */
export const findNextEnabledCalendarYmd = (
  fromDate: string | Date | null | undefined,
  isDisabled: (ymd: string) => boolean,
  maxDays = 60,
): string => {
  const start = toCalendarYmd(fromDate)
  if (!start) return ''
  let ymd = start
  for (let i = 0; i < maxDays; i++) {
    if (!isDisabled(ymd)) return ymd
    const next = addOneCalendarDay(ymd)
    if (!next) return start
    ymd = next
  }
  return start
}
